import * as React from "react";
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import {
  UpfrontPaymentMultipleContainer,
  UpfrontPaymentFieldRow,
  UpfrontPaymentFieldRowTitle,
  UpfrontPaymentInfoRow,
  UpfrontPaymentMultipleContent,
  UpfrontPaymentFinancialAssistance,
  UpfrontPaymentAnswerWrapper,
  UpfrontPaymentMultipleGrid,
  UpfrontPaymentTitle,
  UpfrontPaymentTitleWrapper,
} from "./UpfrontPayment.styles";
import { GetTypographyContent } from "../shared/Typography/Typography";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { BillingSupportNumber } from "./types";
import {
  Bill,
  GetUpfrontPayment,
  getCentsToDollars,
  BillPreview,
} from "./Services/UpfrontPaymentService";

import {
  getBillPreviews,
  getAllRequisitions,
  getBillPreviewsLoading,
  getUserAuth,
} from "../../recoil/selectors";
import { failedPaymentBillPkState } from "../../recoil/atoms";
import { BILLING_URL } from "../../utils/urlConstants";
import { StatusDetails } from "./StatusDetails";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";

export const UpfrontPaymentMultiple: React.FC = () => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const history = useHistory();
  const requisitions = useRecoilValue(getAllRequisitions);
  const auth = useRecoilValue(getUserAuth);
  const billPreviews = useRecoilValue(getBillPreviews);
  const isLoading = useRecoilValue(getBillPreviewsLoading);
  const failedPaymentBillPk = useRecoilValue(failedPaymentBillPkState);

  useEffect(() => {
    if (auth?.access_token) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.impression,
        auth?.access_token,
        auth?.user_id,
        {
          impression_type: "page_view",
          page_name: "UpfrontBillsMultiplePage",
          total_bills: String(requisitions?.length),
        },
      );
    }
  }, []);

  useEffect(() => {
    if (auth?.access_token && billPreviews.length > 0) {
      billPreviews.forEach((billPreview: BillPreview) => {
        TrackAnalyticsEvent(
          AnalyticsEventNames.impression,
          auth.access_token,
          auth.user_id,
          {
            impression_type: "page_view",
            page_name: "UpfrontBillsMultipleSingleBilInfo",
            single_bill_status: billPreview.portal_bill_status,
          },
        );
      });
    }
  }, [billPreviews, auth?.access_token]);

  if (isLoading) {
    return <SpinnerLoading />;
  }

  return (
    <div style={{ display: "grid" }}>
      <UpfrontPaymentTitle
        style={{
          marginTop: deviceType === "mobile" ? "53px" : "47px",
          marginBottom: "0px",
          justifySelf: deviceType !== "mobile" ? "center" : "start",
          width: deviceType !== "mobile" ? "610px" : "325px",
        }}
      >
        <UpfrontPaymentTitleWrapper>
          {GetTypographyContent({
            content: t("upfrontPayment.multipleBills.yourBills"),
            size: "h2",
            textColor: "greyMedium",
            isBold: true,
            padding: "0",
          })}
        </UpfrontPaymentTitleWrapper>
      </UpfrontPaymentTitle>
      {billPreviews &&
        billPreviews.map((billPreview: BillPreview) => {
          const isPastDue =
            billPreview.due_date && new Date(billPreview.due_date) < new Date();
          const isPaid =
            billPreview.portal_bill_status === "paid_in_full" ||
            billPreview.portal_bill_status === "refunded";
          return (
            <UpfrontPaymentMultipleContainer
              deviceType={deviceType}
              key={billPreview.pk}
            >
              <UpfrontPaymentMultipleGrid>
                <UpfrontPaymentMultipleContent>
                  {t("upfrontPayment.multipleBills.billForRequisition", {
                    requisition: billPreview?.req_identifier,
                  })}{" "}
                </UpfrontPaymentMultipleContent>
              </UpfrontPaymentMultipleGrid>
              <>
                <UpfrontPaymentInfoRow adjustable>
                  <>
                    {billPreview.portal_bill_status !== "paid_in_full" &&
                      billPreview.portal_bill_status !== "refunded" && (
                        <UpfrontPaymentFieldRow>
                          <UpfrontPaymentFieldRowTitle>
                            {t("upfrontPayment.multipleBills.amountOwed")}{" "}
                          </UpfrontPaymentFieldRowTitle>
                          {GetTypographyContent({
                            content: getCentsToDollars(billPreview?.balance),
                            size: "medium",
                            textColor: "black",
                          })}
                        </UpfrontPaymentFieldRow>
                      )}
                    <UpfrontPaymentFieldRow>
                      <UpfrontPaymentFieldRowTitle>
                        {t("upfrontPayment.multipleBills.paymentStatus")}{" "}
                      </UpfrontPaymentFieldRowTitle>
                      <StatusDetails
                        billPreview={billPreview}
                        paymentSubmitted={false}
                        isPastDue={Boolean(isPastDue)}
                        hasFailedPayment={
                          failedPaymentBillPk === billPreview?.pk
                        }
                      />
                    </UpfrontPaymentFieldRow>
                  </>
                </UpfrontPaymentInfoRow>
              </>
              <>
                <UpfrontPaymentInfoRow adjustable>
                  <>
                    <UpfrontPaymentFieldRow>
                      <UpfrontPaymentFieldRowTitle>
                        {t("upfrontPayment.receipt.dateOfService")}{" "}
                      </UpfrontPaymentFieldRowTitle>

                      {GetTypographyContent({
                        content: billPreview?.date_of_service,
                        size: "medium",
                        textColor: "black",
                      })}
                    </UpfrontPaymentFieldRow>
                  </>
                </UpfrontPaymentInfoRow>
              </>
              <UpfrontPaymentFinancialAssistance
                onClick={() => {
                  history.push(`${BILLING_URL}/${billPreview?.pk}`);
                }}
              >
                {t("upfrontPayment.multipleBills.viewDetails")}
              </UpfrontPaymentFinancialAssistance>
            </UpfrontPaymentMultipleContainer>
          );
        })}
      <UpfrontPaymentAnswerWrapper
        deviceType={deviceType}
        style={{ justifySelf: "center" }}
      >
        {GetTypographyContent({
          content: t("upfrontPayment.multipleBills.billingQuestionsPrefix"),
          size: "medium",
        })}
        {GetTypographyContent({
          content: BillingSupportNumber,
          size: "medium",
          isPhone: true,
          textColor: "greenDark",
        })}
        {GetTypographyContent({
          content: t("upfrontPayment.multipleBills.billingQuestionsMiddle"),
          size: "medium",
        })}
        {GetTypographyContent({
          content: "billingsupport@billiontoone.com",
          size: "medium",
          isEmail: true,
          textColor: "greenDark",
        })}
      </UpfrontPaymentAnswerWrapper>
    </div>
  );
};
